import React, { useMemo, useState } from "react";
import { Moon, Sun, Sparkles, Loader2 } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { Reveal } from "../components/Reveal";
import { FestivalCard } from "../components/FestivalStrip";
import CountdownBanner from "../components/CountdownBanner";
import { useLang } from "../i18n/LanguageContext";
import { SITE } from "../i18n/translations";
import { useFestivals, festivalName, formatFestivalDate } from "../lib/festivals";

const FILTERS = [
  { id: "all", label: "All dates", icon: Sparkles },
  { id: "amavasya", label: "Amavasya", icon: Moon },
  { id: "purnima", label: "Purnima", icon: Sun },
  { id: "major", label: "Major nights", icon: Sparkles },
];

export default function Festivals() {
  const { t, lang } = useLang();
  const { items, loading } = useFestivals(60);
  const [filter, setFilter] = useState("all");

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    if (filter === "major") return items.filter((f) => f.major);
    if (filter === "amavasya") return items.filter((f) => f.kind !== "purnima");
    return items.filter((f) => f.kind === "purnima");
  }, [items, filter]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((f) => {
      const key = new Date(`${f.date}T00:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" });
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    });
    return [...map.entries()];
  }, [filtered]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": items.slice(0, 12).map((f) => ({
      "@type": "Event",
      name: `${f.name} at Tarapith`,
      startDate: f.date,
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      location: { "@type": "Place", name: "Tarapith, Birbhum, West Bengal, India" },
      description: f.note,
      organizer: { "@type": "Person", name: SITE.practitioner },
    })),
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="festivals-page">
      <Seo
        title="Amavasya & Festival Calendar at Tarapith"
        description="Upcoming Amavasya, Kaushiki Amavasya and Purnima dates computed from the lunar calendar, with one-tap puja enquiry for each observance at Tarapith."
        path="/festivals"
        keywords={["Kaushiki Amavasya date", "Tarapith Amavasya calendar", "Amavasya puja Tarapith", "Purnima dates"]}
        jsonLd={jsonLd}
      />
      <Breadcrumbs items={[{ label: t("nav_festivals") }]} />

      <Reveal variant="blur" className="max-w-2xl mb-10">
        <span className="text-[11px] font-serif tracking-[0.28em] uppercase text-tara-goldDeep">{t("festivals_eyebrow")}</span>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold text-tara-ink mt-3 leading-tight">{t("festivals_title")}</h1>
        <div className="rule-gold my-5" />
        <p className="text-base text-tara-body leading-relaxed">{t("festivals_sub")}</p>
      </Reveal>

      <CountdownBanner variant="inline" />

      <div className="flex flex-wrap gap-2.5 mb-10">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] transition-all ${
              filter === f.id
                ? "bg-gradient-to-r from-tara-gold to-tara-vermilion text-white shadow-soft"
                : "border border-tara-gold/25 bg-white/70 text-tara-body hover:border-tara-gold/50 hover:text-tara-sindoor"
            }`}
            data-testid={`festival-filter-${f.id}`}
          >
            <f.icon className="h-3.5 w-3.5" /> {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-tara-gold" /></div>
      ) : (
        <div className="space-y-14">
          {grouped.map(([month, list]) => (
            <section key={month} data-testid={`festival-month-${month.replace(/\s/g, "-")}`}>
              <div className="flex items-center gap-4 mb-6">
                <h2 className="font-serif text-xl font-semibold text-tara-sindoor whitespace-nowrap">{month}</h2>
                <span className="divider-om flex-1" />
                <span className="text-xs text-tara-muted">{list.length}</span>
              </div>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((f, i) => <FestivalCard key={f.id} f={f} index={i} />)}
              </div>
            </section>
          ))}
        </div>
      )}

      <div className="glass rounded-[1.5rem] p-6 mt-16">
        <p className="text-sm text-tara-body leading-relaxed">
          <strong className="text-tara-sindoor">{t("note")}:</strong> {t("festivals_disclaimer")}
        </p>
        {items[0] && (
          <p className="text-xs text-tara-muted mt-3">
            {t("next_observance")}: {festivalName(items[0], lang)} — {formatFestivalDate(items[0].date)}
          </p>
        )}
      </div>
    </div>
  );
}
